/**
 * TeamPage — manage farm crew (§35 RBAC). Lists members with their roles, seat type
 * and status; managers (owner/admin) can edit a member's roles or remove them. A seat
 * usage card shows full vs farm-hand seats used against the plan's included seats +
 * the monthly overage cost. Mutations are gated server-side by PermManageUsers.
 *
 * NOTE: self-serve email invite is a documented follow-up — there is no working
 * member-invite endpoint yet (both /team/invite and /tenant/members are stubs). Members
 * appear here once they've joined (OAuth onboarding / accepted invite); you assign their
 * roles from this page.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, X, Shield, UserPlus, Link2, Copy, Check, RefreshCw, UserCheck, UserX } from 'lucide-react'
import { teamApi, type TeamMember, type TeamRole, type SeatUsage, type InviteResult, type JoinRequest } from '../lib/api'
import { errStatus } from '../lib/errors'
import { useAuthStore } from '../lib/store'

// Role labels + descriptions + seat type (mirrors backend rbac.RoleMetadata).
const ROLE_META: Record<TeamRole, { label: string; desc: string; seat: 'full' | 'farm_hand' }> = {
  owner:               { label: 'Owner',               desc: 'Full control of the farm. One per account.', seat: 'full' },
  admin:               { label: 'Administrator',       desc: 'Manage users, roles, billing, site, and all modules.', seat: 'full' },
  irrigation_manager:  { label: 'Irrigation Manager',  desc: 'Create/edit irrigation zones, schedules, controllers.', seat: 'full' },
  irrigation_operator: { label: 'Irrigation Operator', desc: 'Run zones manually + view status. No config edits.', seat: 'full' },
  zone_manager:        { label: 'Zone Manager',        desc: 'Manage plant zones, care templates, growable profiles.', seat: 'full' },
  farm_hand:           { label: 'Farm Hand',           desc: 'Log care, observations, harvests. Lower-cost seat.', seat: 'farm_hand' },
  viewer:              { label: 'Viewer',              desc: 'Read-only access to all modules.', seat: 'full' },
}
const roleLabel = (r: TeamRole) => ROLE_META[r]?.label ?? r

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }
const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
const STATUS_COLOR: Record<string, string> = { active: '#16a34a', invited: '#d97706', suspended: '#dc2626', deleted: '#6b7280' }

export function TeamPage() {
  const { role } = useAuthStore()
  const canManage = role === 'owner' || role === 'admin' || role === 'tenant_manager'
  const [editing, setEditing] = useState<TeamMember | null>(null)
  const [inviting, setInviting] = useState(false)

  const { data: members } = useQuery({ queryKey: ['team'], queryFn: () => teamApi.list().then(r => r.data) })
  const { data: seats } = useQuery({ queryKey: ['team-seats'], queryFn: () => teamApi.seats().then(r => r.data) })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={20} /> Team
        </h1>
        {canManage && (
          <button onClick={() => setInviting(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
            <UserPlus size={15} /> Invite member
          </button>
        )}
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 18 }}>
        Your farm crew and what each person can do. {canManage ? 'Invite teammates and set their roles.' : 'Ask an owner or admin to change roles.'}
      </p>

      {seats && <SeatCard seats={seats} />}

      {canManage && <JoinRequestsCard />}

      <div style={{ ...card, marginTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>Members</div>
        {!members?.length ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No members yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map(m => (
              <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--color-bg)' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'color-mix(in srgb, var(--color-primary) 18%, transparent)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0, overflow: 'hidden' }}>
                  {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (m.display_name || m.email || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.display_name || m.email}
                    <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, color: STATUS_COLOR[m.status] ?? 'var(--color-text-muted)' }}>● {m.status}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {(m.roles?.length ? m.roles : (['viewer'] as TeamRole[])).map(r => (
                      <span key={r} style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', color: 'var(--color-primary)' }}>{roleLabel(r)}</span>
                    ))}
                  </div>
                </div>
                <span title={`${m.seat_type === 'farm_hand' ? 'Farm-hand' : 'Full'} seat`} style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--color-text-muted)', flexShrink: 0 }}>
                  {m.seat_type === 'farm_hand' ? 'Farm-hand seat' : 'Full seat'}
                </span>
                {canManage && (
                  <button onClick={() => setEditing(m)} style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: 'var(--color-text)', cursor: 'pointer' }}>
                    Manage
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 12, lineHeight: 1.5 }}>
          {canManage ? 'Use “Invite member” to email someone directly, or share an invite link below. They appear here once added.' : 'New crew members appear here after they join your farm.'}
        </p>
      </div>

      {canManage && <InviteLinkCard />}

      {/* Role reference */}
      <div style={{ ...card, marginTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Shield size={14} /> Roles
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {(Object.keys(ROLE_META) as TeamRole[]).map(r => (
            <div key={r} style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                {ROLE_META[r].label}
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)' }}>{ROLE_META[r].seat === 'farm_hand' ? 'farm-hand seat' : 'full seat'}</span>
              </div>
              <div style={{ color: 'var(--color-text-muted)', marginTop: 1 }}>{ROLE_META[r].desc}</div>
            </div>
          ))}
        </div>
      </div>

      {editing && <RolesModal member={editing} onClose={() => setEditing(null)} />}
      {inviting && <InviteModal onClose={() => setInviting(false)} />}
    </main>
  )
}

// Pending join requests — people who opened the shareable link + signed in (§206).
// Approve with a role (→ membership) or reject. Hidden when there are none.
function JoinRequestsCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['join-requests'], queryFn: () => teamApi.joinRequests().then(r => r.data.requests), refetchInterval: 30_000 })
  const [roleFor, setRoleFor] = useState<Record<string, TeamRole>>({})
  const [needsEnt, setNeedsEnt] = useState<Record<string, boolean>>({})
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['join-requests'] }); qc.invalidateQueries({ queryKey: ['team'] }); qc.invalidateQueries({ queryKey: ['team-seats'] }) }
  const approve = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole; email: string }) => teamApi.approveJoinRequest(userId, role),
    onSuccess: (_d, v) => { setNeedsEnt(s => ({ ...s, [v.userId]: false })); invalidate() },
    // 409 = §88.1 multi-workspace gate — offer to sponsor a seat then approve.
    onError: (e, v) => { if (errStatus(e) === 409) setNeedsEnt(s => ({ ...s, [v.userId]: true })) },
  })
  // Sponsor a multi-workspace seat for this person (billed through this farm), then approve.
  const sponsorApprove = useMutation({
    mutationFn: async ({ userId, role, email }: { userId: string; role: TeamRole; email: string }) => {
      await teamApi.sponsorAddon(email)
      return teamApi.approveJoinRequest(userId, role)
    },
    onSuccess: (_d, v) => { setNeedsEnt(s => ({ ...s, [v.userId]: false })); invalidate() },
  })
  const reject = useMutation({
    mutationFn: (userId: string) => teamApi.rejectJoinRequest(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['join-requests'] }),
  })
  const reqs = data ?? []
  if (reqs.length === 0) return null
  return (
    <div style={{ ...card, marginTop: 18, borderColor: 'color-mix(in srgb, var(--color-primary) 35%, var(--color-border))' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <UserCheck size={14} /> Requests to join ({reqs.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {reqs.map((req: JoinRequest) => {
          const sel = roleFor[req.user_id] ?? 'farm_hand'
          return (
            <div key={req.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--color-bg)' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{req.display_name || req.email}</div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  {req.email} · {req.provider} · {new Date(req.requested_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </div>
              </div>
              <select value={sel} onChange={e => setRoleFor(s => ({ ...s, [req.user_id]: e.target.value as TeamRole }))}
                style={{ ...inp, width: 150 }}>
                {(Object.keys(ROLE_META) as TeamRole[]).filter(r => r !== 'owner').map(r => (
                  <option key={r} value={r}>{ROLE_META[r].label}</option>
                ))}
              </select>
              {needsEnt[req.user_id] ? (
                <button onClick={() => sponsorApprove.mutate({ userId: req.user_id, role: sel, email: req.email })} disabled={sponsorApprove.isPending}
                  title="This person is in another free workspace; sponsoring a multi-workspace seat (billed to this farm) lets them join."
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                  <UserCheck size={13} /> {sponsorApprove.isPending ? 'Sponsoring…' : 'Sponsor seat & approve'}
                </button>
              ) : (
                <button onClick={() => approve.mutate({ userId: req.user_id, role: sel, email: req.email })} disabled={approve.isPending}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                  <UserCheck size={13} /> Approve
                </button>
              )}
              <button onClick={() => { if (confirm(`Reject ${req.display_name || req.email}?`)) reject.mutate(req.user_id) }} disabled={reject.isPending}
                title="Reject" style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: '#dc2626', cursor: 'pointer' }}>
                <UserX size={13} />
              </button>
              {needsEnt[req.user_id] && (
                <div style={{ flexBasis: '100%', fontSize: 11.5, color: '#d97706' }}>
                  Already in another free workspace — sponsoring a multi-workspace seat (billed to this farm) lets them join.
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Shareable invite link — anyone with the link can sign in and request to join (§206).
function InviteLinkCard() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['invite-link'], queryFn: () => teamApi.getInviteLink().then(r => r.data) })
  const [copied, setCopied] = useState(false)
  const gen = useMutation({
    mutationFn: () => teamApi.generateInviteLink(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invite-link'] }),
  })
  const url = data?.url || ''
  function copy() {
    if (!url) return
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }
  return (
    <div style={{ ...card, marginTop: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link2 size={14} /> Shareable invite link
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 0, marginBottom: 10 }}>
        Anyone with this link can sign in (Google, Apple, or email) and request to join — they appear above for you to approve with a role.
      </p>
      {isLoading ? (
        <p style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>Loading…</p>
      ) : url ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ flex: 1, minWidth: 220, padding: '8px 10px', fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</code>
          <button onClick={copy} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 12.5, fontWeight: 600 }}>
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
          </button>
          <button onClick={() => gen.mutate()} disabled={gen.isPending} title="Generate a new link (invalidates the old one)"
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: 'var(--color-text)', cursor: 'pointer', fontSize: 12.5 }}>
            <RefreshCw size={13} /> Regenerate
          </button>
        </div>
      ) : (
        <button onClick={() => gen.mutate()} disabled={gen.isPending}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          <Link2 size={14} /> {gen.isPending ? 'Generating…' : 'Generate invite link'}
        </button>
      )}
      {data?.expires_at && url && (
        <p style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 8 }}>
          Expires {new Date(data.expires_at).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })} · Regenerating invalidates the old link.
        </p>
      )}
    </div>
  )
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [roles, setRoles] = useState<TeamRole[]>(['farm_hand'])
  const [result, setResult] = useState<InviteResult | null>(null)
  const toggle = (r: TeamRole) => setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])

  const send = useMutation({
    mutationFn: () => teamApi.invite({ email: email.trim(), name: name.trim() || undefined, roles }).then(r => r.data),
    onSuccess: (res) => { setResult(res); qc.invalidateQueries({ queryKey: ['team'] }); qc.invalidateQueries({ queryKey: ['team-seats'] }) },
  })
  // When blocked on the multi-workspace entitlement: sponsor an add-on for this
  // person (billed through this farm), then re-invite — one click resolves it.
  const sponsorAndInvite = useMutation({
    mutationFn: async () => { await teamApi.sponsorAddon(email.trim()); return teamApi.invite({ email: email.trim(), name: name.trim() || undefined, roles }).then(r => r.data) },
    onSuccess: (res) => { setResult(res); qc.invalidateQueries({ queryKey: ['team'] }); qc.invalidateQueries({ queryKey: ['team-seats'] }) },
  })
  const emailValid = /\S+@\S+\.\S+/.test(email.trim())

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>Invite a member</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>

        {result?.status === 'needs_entitlement' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--color-text)' }}>
              <strong>{result.email}</strong> already belongs to another Grafty workspace on a free plan, so they need a
              <strong> multi-workspace seat</strong> to also join your farm{result.reason ? ` (${result.reason})` : ''}.
            </p>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              You can sponsor that seat — it’s billed through your farm, not their card — and we’ll add them right away.
            </p>
            {sponsorAndInvite.isError && <p style={{ fontSize: 12, color: '#dc2626' }}>Could not sponsor — {(sponsorAndInvite.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'try again'}.</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
              <button onClick={() => sponsorAndInvite.mutate()} disabled={sponsorAndInvite.isPending} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                {sponsorAndInvite.isPending ? 'Adding…' : 'Sponsor seat & invite'}
              </button>
            </div>
          </div>
        ) : result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--color-text)' }}>
              {result.status === 'invited'
                ? <>Invited <strong>{result.email}</strong>. {result.email_sent ? 'A sign-up link was emailed to them.' : 'Email isn’t configured here — share the sign-up link from their account, or they can use “Forgot password”.'}</>
                : <><strong>{result.email}</strong> already has a Grafty account and was added to your farm. They’ll see it next time they sign in.</>}
            </p>
            <button onClick={onClose} style={{ alignSelf: 'flex-end', padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>Done</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div><label style={lbl}>Email *</label><input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="crew@example.com" autoFocus /></div>
            <div><label style={lbl}>Name (optional)</label><input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Their name" /></div>
            <div>
              <label style={lbl}>Roles (a member can hold several)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(Object.keys(ROLE_META) as TeamRole[]).filter(r => r !== 'owner').map(r => (
                  <label key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 9px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${roles.includes(r) ? 'color-mix(in srgb, var(--color-primary) 45%, transparent)' : 'var(--color-border)'}`, background: roles.includes(r) ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'var(--color-bg)' }}>
                    <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(r)} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)' }}>{ROLE_META[r].label}
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)' }}>{ROLE_META[r].seat === 'farm_hand' ? 'farm-hand' : 'full'}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{ROLE_META[r].desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p style={{ fontSize: 10.5, color: 'var(--color-text-faint)', marginTop: 4 }}>No role selected = Viewer (read-only).</p>
            </div>
            {send.isError && <p style={{ fontSize: 12, color: '#dc2626' }}>Could not invite — {(send.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'try again'}.</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
              <button onClick={() => send.mutate()} disabled={send.isPending || !emailValid} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: send.isPending || !emailValid ? 'not-allowed' : 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, opacity: !emailValid ? 0.5 : 1 }}>
                {send.isPending ? 'Inviting…' : 'Send invite'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SeatCard({ seats }: { seats: SeatUsage }) {
  const Bar = ({ label, used, included, price }: { label: string; used: number; included: number; price: number }) => {
    const over = Math.max(0, used - included)
    const pct = seats.unlimited ? 0 : included > 0 ? Math.min(100, (used / included) * 100) : (used > 0 ? 100 : 0)
    const color = over > 0 ? '#dc2626' : pct >= 80 ? '#d97706' : '#16a34a'
    return (
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{label}</span>
          <span style={{ color: 'var(--color-text-muted)' }}>{seats.unlimited ? `${used} · unlimited` : `${used} / ${included}`}</span>
        </div>
        {!seats.unlimited && (
          <div style={{ height: 6, borderRadius: 3, background: 'var(--color-bg)', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color }} />
          </div>
        )}
        {over > 0 && <div style={{ fontSize: 10.5, color: '#dc2626', marginTop: 3 }}>+{over} over · {usd(over * price)}/mo</div>}
      </div>
    )
  }
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Seats</span>
        {seats.unlimited ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a' }}>Unlimited (Estate)</span>
        ) : seats.monthly_overage_usd > 0 ? (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>+{usd(seats.monthly_overage_usd)}/mo overage</span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>within plan</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <Bar label="Full seats" used={seats.used_full_seats} included={seats.included_full_seats} price={seats.full_seat_price_usd} />
        <Bar label="Farm-hand seats" used={seats.used_farmhand_seats} included={seats.included_farmhand_seats} price={seats.farmhand_seat_price_usd} />
      </div>
    </div>
  )
}

function RolesModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const qc = useQueryClient()
  const [roles, setRoles] = useState<TeamRole[]>(member.roles ?? [])
  const toggle = (r: TeamRole) => setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])

  const save = useMutation({
    mutationFn: () => teamApi.setRoles(member.user_id, roles),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['team'] }); qc.invalidateQueries({ queryKey: ['team-seats'] }); onClose() },
  })
  const remove = useMutation({
    mutationFn: () => teamApi.remove(member.user_id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['team'] }); qc.invalidateQueries({ queryKey: ['team-seats'] }); onClose() },
  })
  const isOwner = member.roles?.includes('owner')

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{member.display_name || member.email}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 14 }}>{member.email}</p>
        <label style={lbl}>Roles (a member can hold several)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {(Object.keys(ROLE_META) as TeamRole[]).map(r => (
            <label key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 9px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${roles.includes(r) ? 'color-mix(in srgb, var(--color-primary) 45%, transparent)' : 'var(--color-border)'}`, background: roles.includes(r) ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'var(--color-bg)' }}>
              <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(r)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)' }}>{ROLE_META[r].label}
                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)' }}>{ROLE_META[r].seat === 'farm_hand' ? 'farm-hand' : 'full'}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{ROLE_META[r].desc}</div>
              </div>
            </label>
          ))}
        </div>
        {isOwner && <p style={{ fontSize: 11, color: '#d97706', marginBottom: 10 }}>This member is an owner. Be careful removing owner access.</p>}
        {(save.isError || remove.isError) && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>Could not save — you may not have permission, or try again.</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button onClick={() => { if (confirm(`Remove ${member.display_name || member.email} from the farm?`)) remove.mutate() }} disabled={remove.isPending}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid color-mix(in srgb, #dc2626 45%, transparent)', background: 'none', color: '#dc2626', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            {remove.isPending ? 'Removing…' : 'Remove member'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : 'Save roles'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamPage
